import { Button, Card, Col, Form, Input, message, Modal, Row, Spin } from 'antd';
import React from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { formatDisplayDate, ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import moment from 'moment';
import BusInsurerInput from '@/components/FormItems/BusInsurerInput';
import AppDatePicker from '@/components/FormItems/DatePicker';
import { clearAllBussInsurersCache } from '@/hooks/data/masterDataHook';
import { BusInsuranceDto, BusDocumentDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  onClose: () => void;
  busId: string;
};

const columns: IColumnType<BusInsuranceDto>[] = [
  {
    title: 'busesPage_PolicyNumber',
    dataIndex: 'policyNumber',
    ellipsis: true,
    sorter: true,
  },
  {
    title: 'busesPage_StartDate',
    dataIndex: 'startDate',
    sorter: true,
    render: v => formatDisplayDate(v),
  },
  {
    title: 'busesPage_ExpirationDate',
    dataIndex: 'expiryDate',
    ellipsis: true,
    sorter: true,
    render: v => formatDisplayDate(v),
  },
  {
    title: 'base.Status',
    dataIndex: 'expiryDate',
    sorter: true,
    render: v => {
      return moment(v) < moment() ? 'Expire' : 'Active';
    },
  },
];

const filters: Array<IFilterItem> = [
  {
    type: 'TEXT',
    name: 'policyNumber',
    label: 'busesPage_PolicyNumber',
    mdSize: 12,
    xlSize: 12,
    xxlSize: 12,
  },
];
export default function BusInsuranceModal({ onClose, busId }: Props) {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  const clearCache = () => {
    clearAllBussInsurersCache();
  };

  const onFormSumit = (values: any) => {
    setLoading(true);
    apiSdk.BusInsuranceService.create({
      body: { ...values, busId: busId },
    })
      .then(response => {
        if (response?.success) {
          zTb.current?.reload();
          form.resetFields();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      style={{ top: 10 }}
      open={true}
      maskClosable={false}
      width={ModalWidth.medium}
      className="nta-custom-form"
      title={t('busesPage_BusInsurances')}
      forceRender={true}
      okText={t('save')}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
    >
      <Spin spinning={loading}>
        <Card className="mtopo-card" size="small" title={t('busesPage_AddBisInsurancePolicy')}>
          <Form form={form} layout="vertical" onFinish={onFormSumit}>
            <Row gutter={ROW_GUTTER}>
              <Col md={12}>
                <Form.Item label={t('busesPage_PolicyNumber')} name="policyNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item label={t('busesPage_BusInsurances')} name="busInsurerId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <BusInsurerInput />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item noStyle dependencies={['expiryDate']}>
                  {({ getFieldValue }) => {
                    const expiryDate = getFieldValue('expiryDate');
                    return (
                      <Form.Item label={t('busesPage_StartDate')} name="startDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <AppDatePicker
                          className="w-100"
                          disabledDate={d => {
                            if (expiryDate) {
                              return d.endOf('day') > moment(expiryDate).endOf('day');
                            }
                            return false;
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item noStyle dependencies={['startDate']}>
                  {({ getFieldValue }) => {
                    const startDate = getFieldValue('startDate');
                    return (
                      <Form.Item label={t('busesPage_ExpirationDate')} name="expiryDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <AppDatePicker
                          className="w-100"
                          disabledDate={d => {
                            if (startDate) {
                              return d.endOf('day') < moment(startDate).endOf('day');
                            }
                            return false;
                          }}
                        />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
              <Col md={24} style={{ textAlign: 'end' }}>
                <Button type="primary" htmlType="submit">
                  {t('save')}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>
      </Spin>
      <Card className="mtopo-card" size="small" title={t('busesPage_InsuranceDetail')}>
        <ZTable<BusDocumentDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          defaultFilter={{
            busId: busId,
          }}
          url="/fleets/bus-insurances/get-bus-insurances"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={false}
          allowDelete={false}
          filters={filters}
          searchBtnAlignLeft
          onDeleteCompleted={clearCache}
        ></ZTable>
      </Card>
    </Modal>
  );
}
